import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { groupBy, uniqueBy } from '@lassi/util';
import { GeoJSON, LatLngBounds, Point } from 'leaflet';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import {
  GeoserverCRS,
  GeoserverQueryResult,
  IBulkDownloadableFeature,
  IdentifyFeatureType,
  IMapFeature,
  IParcelFeature,
  IPlaceFeature,
  IRoadFeature,
  ISurveyLabelFeature,
  ISurveyMarkFeature,
} from './feature.model';
import { LayerManagementService } from './layer-management.service';
import { isLayerInactive, MapLayer } from './layers';
import { MapService, wfsUrl, wmsUrl } from './map';
import { VicGridCRS } from './projections';

type QueryLayer =
  | ({ id: string } & ({ query: string[] } | { prefix: string }))
  | string;
type QueryLayerMap = Record<
  'parcel' | 'surveyLabel' | 'surveyMark' | 'place' | 'road' | 'ohap',
  Array<QueryLayer>
>;

@Injectable({
  providedIn: 'root',
})
export class FeatureService {
  private readonly http = inject(HttpClient);

  private readonly scaleFactor$ = inject(MapService).scaleFactor$;
  private readonly layersMap$ = inject(
    LayerManagementService,
  ).activeMapLayers$.pipe(
    map((layers) => this.reduceLayersToMap(layers)),
    shareReplay(1),
  );

  private reduceLayersToMap(
    layers: ((MapLayer & { status: boolean }) | null)[],
  ): Record<
    MapLayer['name'],
    Pick<MapLayer, 'minScale' | 'maxScale'> & { status: boolean }
  > {
    return layers.reduce(
      (acc, cur) => {
        if (!cur) {
          return acc;
        }
        acc[cur.name] = {
          minScale: cur.minScale,
          maxScale: cur.maxScale,
          status: cur.status,
        };
        return acc;
      },
      {} as Record<
        MapLayer['name'],
        Pick<MapLayer, 'minScale' | 'maxScale'> & { status: boolean }
      >,
    );
  }

  private readonly wfsBaseUrl = wfsUrl;
  readonly wmsBaseUrl = wmsUrl;

  readonly queryLayerMap: QueryLayerMap = {
    parcel: [
      {
        id: 'PARCEL',
        query: ['PARCEL_MP_APPROVED'],
      },
      {
        id: 'PROPERTY',
        query: ['PARCEL_MP_APPROVED'],
      },
      {
        id: 'PARCEL_ABOVE_SURFACE',
        query: ['PARCEL_ABOVE'],
      },
      {
        id: 'PARCEL_SURFACE',
        query: ['PARCEL_SURF'],
      },
      {
        id: 'PARCEL_BELOW_SURFACE',
        query: ['PARCEL_BELOW'],
      },
      {
        id: 'PARCEL_ROAD_GROUP',
        query: ['PARCEL_ROAD'],
      },
      {
        id: 'PROPOSED',
        query: ['PROPOSED_PARCEL'],
      },
    ],
    surveyLabel: [
      'SURVEY_LABEL',
      'RETIRED_SURVEY_LABEL',
      'PLAN',
      'ROADS_CORP_SP_WITHOUT_FN',
      'ROADS_CORP_SP_WITH_FN',
      'ROADS_CORP_CONTROL_PLANS',
    ],
    surveyMark: [
      {
        id: 'SMES_SCN_GDA94',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_SCN_AHD',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_NON_SCN_GDA94',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_NON_SCN_AHD',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_LEVEL',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_GNSS',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_EST',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_DEFECTIVE',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_PCM',
        prefix: 'IDENTIFY_',
      },
    ],
    place: ['PLACE_NAMES'],
    road: [
      'ROADS_CENTRELINES',
      { id: 'PLACE_NAMES', query: ['ROADS_CENTRELINES'] },
    ],
    ohap: [
      'AERIAL_FRAME_POINT_1990S',
      'AERIAL_FRAME_POINT_1980S',
      'AERIAL_FRAME_POINT_1970S',
      'AERIAL_FRAME_POINT_1960S',
      'AERIAL_FRAME_POINT_1950S',
      'AERIAL_FRAME_POINT_1940S',
      'AERIAL_FRAME_POINT_1930S',
    ],
  };

  private readonly bulkDownloadQueryLayerMap: QueryLayerMap = {
    parcel: [],
    surveyLabel: [
      { id: 'SURVEY_LABEL', query: ['LTO', 'VMAP_LV_CPO'] },
      'RETIRED_SURVEY_LABEL',
      'PLAN',
      'ROADS_CORP_SP_WITHOUT_FN',
      'ROADS_CORP_SP_WITH_FN',
      'ROADS_CORP_CONTROL_PLANS',
    ],
    surveyMark: [
      {
        id: 'SMES_SCN_GDA94',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_SCN_AHD',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_NON_SCN_GDA94',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_NON_SCN_AHD',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_LEVEL',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_GNSS',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_AVWS_EST',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_DEFECTIVE',
        prefix: 'IDENTIFY_',
      },
      {
        id: 'SMES_PCM',
        prefix: 'IDENTIFY_',
      },
    ],
    place: [],
    road: [],
    ohap: [],
  };

  private readonly broadcastMsgAudienceLayerMap: QueryLayerMap = {
    parcel: [
      ...this.queryLayerMap.parcel,
      'PROPERTY_ADDRESS_NUMBERS',
      'SPEAR_CROWN_ALLOTMENT',
      'SPEAR_CROWN_LAND',
      'LOT_NUMBERS',
      'PARCEL_PLAN',
      'PARCEL_SPI',
    ],
    surveyLabel: [
      ...this.queryLayerMap.surveyLabel,
      'PARISH_TOWNSHIP',
      'BUA_PROPOSED_ANNO',
      'PROC_SURVEY_AREA',
      'BUILT_UP_AREA',
      'PARISH_ZONE_BOUNDARY',
      'DUAL_UTM_ZONE_BOUNDARY_AND_BUFFER',
    ],
    surveyMark: [...this.queryLayerMap.surveyMark],
    place: [
      ...this.queryLayerMap.place,
      'FOI_POINT_NOVID',
      'FOI_LINE_NOVID',
      'FOI_POLYGON_NOVID',
      'PLM25_EXTENTS',
      'CUSTOM_RADIUS',
    ],
    road: [],
    ohap: [],
  };

  private processQueryLayer(queryLayer: QueryLayer) {
    if (typeof queryLayer === 'string') {
      return [queryLayer];
    }
    if ('prefix' in queryLayer) {
      return [queryLayer.prefix + queryLayer.id];
    }

    return queryLayer.query;
  }

  public activeDocStatusLayers$ = combineLatest([
    this.getQueryLayers('surveyLabel', this.queryLayerMap),
    this.getQueryLayers('surveyMark', this.queryLayerMap),
  ]).pipe(debounceTime(100));

  private getQueryLayers(
    key: keyof QueryLayerMap,
    queryMap: QueryLayerMap,
    ignoreStatus = false,
  ) {
    return combineLatest([this.layersMap$, this.scaleFactor$]).pipe(
      map(([activeLayers, scale]) =>
        Array.from(
          new Set(
            queryMap[key]
              .filter((queryLayer) => {
                const activeLayerId =
                  typeof queryLayer === 'string' ? queryLayer : queryLayer.id;

                const activeLayer = activeLayers[activeLayerId];
                if (typeof activeLayers[activeLayerId] === 'undefined') {
                  return false;
                }

                return (
                  ignoreStatus ||
                  (activeLayer.status && !isLayerInactive(activeLayer, scale))
                );
              })
              .map((queryLayer) => this.processQueryLayer(queryLayer))
              .flat(),
          ),
        ),
      ),
      map((layers) => layers.join(',')),
      distinctUntilChanged(),
    );
  }

  public getWMSParams(
    mapBounds: LatLngBounds,
    mapSize: Point,
    clickPosition: Point,
    layers: string,
  ) {
    const bboxArray = mapBounds
      .toBBoxString()
      .split(',')
      .map((i) => parseFloat(i));

    const p1 = VicGridCRS.project({
      lat: bboxArray[1],
      lng: bboxArray[0],
    });
    // project second coord pair
    const p2 = VicGridCRS.project({
      lat: bboxArray[3],
      lng: bboxArray[2],
    });
    const projectedBbox = [p1.x, p1.y, p2.x, p2.y].join(',');
    return {
      service: 'wms', // required
      version: '1.1.1', // required - match to geoserver version - current geolassi should be 1.1.1
      srs: 'EPSG:7899', // required - should be SRS clicked coords, i.e. wgs84 EPSG:4326
      request: 'GetFeatureInfo', // required
      bbox: projectedBbox, // required - bbox of map
      height: mapSize.y.toString(), // required - height of MAP in PIXELS
      width: mapSize.x.toString(), // required - width of MAP in PIXELS
      x: clickPosition.x.toString(), // required - clicked location X in PIXELS
      y: clickPosition.y.toString(), // required - clicked location Y in PIXELS
      buffer: '20', // suggested - buffer area in pixels, to allow for some margin in user click
      info_format: 'application/json', // suggested - other options exist but JSON best for web
      feature_count: '999', // suggested - set to appropriate max - otherwise defaults to 1
      layers: layers, // required - required by standard, but has no effect of GetFeatureInfo query. Just set to same as 'layers'
      query_layers: layers, // required - list of layers to query for features
    };
  }

  private boundsToBBox(latLngBounds: LatLngBounds) {
    const bboxArray = latLngBounds
      .toBBoxString()
      .split(',')
      .map((i) => parseFloat(i));

    const p1 = VicGridCRS.project({
      lat: bboxArray[1],
      lng: bboxArray[0],
    });
    // project second coord pair
    const p2 = VicGridCRS.project({
      lat: bboxArray[3],
      lng: bboxArray[2],
    });
    return [p1.x, p1.y, p2.x, p2.y].join(',');
  }

  private getWfsParams(layers: string, limit?: number) {
    return {
      service: 'wfs', // required
      version: '2.0.0', // required - match to geoserver version - current geolassi should be 2.0.0
      request: 'GetFeature', // required
      typeName: layers, // required - list of layers to query for features.
      // must also be prefixed with namespace, for Lassi this is 'LASSI_WS'
      // INTERSECTS = test if geometries overlap
      outputformat: 'json', // suggested - other options exist but JSON best for web
      propertyName: '*', // suggested - list of fields to be returned - include SHAPE to get geometry, otherwise will be excluded
      ...(typeof limit === 'number' ? { count: limit.toString() } : {}), // suggested - limit of features returned
    };
  }

  private getIdentifyWfsParams(
    pointOrExtent: Point | LatLngBounds,
    layers: string,
    limit?: number,
  ) {
    return {
      ...this.getWfsParams(layers, limit),
      cql_filter:
        pointOrExtent instanceof Point
          ? `contains ( shape , point (${pointOrExtent.x} ${pointOrExtent.y}) )`
          : `BBOX(shape, ${this.boundsToBBox(pointOrExtent)})`, // not required for request, but required for spatial filtering
    };
  }

  private getParcelFeatureInfo(
    feature: GeoJSON.Feature<GeoJSON.MultiPolygon>,
  ): {
    spi: string;
    id: string | number;
    vPfi: string;
  } {
    if (feature.id?.toString().includes('PARCEL_MP_APPROVED')) {
      return {
        id: feature.properties?.['parcel_pfi'],
        spi: feature.properties?.['parcel_spi'],
        vPfi: feature.properties?.['parv_pfi'],
      };
    }

    return {
      id: feature.properties?.['pv_pfi'],
      spi: feature.properties?.['spi'],
      vPfi: feature.properties?.['parv_pfi'],
    };
  }

  private parcelWfsQuery(
    params: ReturnType<typeof this.getIdentifyWfsParams>,
    point: Point,
  ) {
    const searchParams = new URLSearchParams(params);
    const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

    return this.http.get<GeoserverQueryResult<GeoJSON.MultiPolygon>>(url).pipe(
      map((result): IParcelFeature | null => {
        if (result.features.length === 0) {
          return null;
        }

        const uniqueFeatures = uniqueBy(
          result.features.map((feature) => this.getParcelFeatureInfo(feature)),
          'spi',
        );

        const uniqueViews = uniqueBy(
          result.features.map((feature) => ({
            ...this.getParcelFeatureInfo(feature),
            geometry: feature.geometry,
          })),
          'vPfi',
        );

        return {
          type: IdentifyFeatureType.Parcel,
          geometry: {
            type: 'MultiPolygon',
            coordinates: uniqueViews.flatMap(
              ({ geometry }) => geometry.coordinates,
            ),
          },
          parcels: uniqueFeatures.map((feature) => ({
            id: feature.id,
            spi: feature.spi,
          })),
          coords: point,
          crs: result.crs,
          vPfis: uniqueViews.map((f) => f.vPfi),
        };
      }),
    );
  }

  public getParcelFeatures(point: Point) {
    const params = this.getIdentifyWfsParams(point, this.PARCEL_QUERY_LAYER, 1);

    return this.parcelWfsQuery(params, point);
  }

  public getIdentifyParcelFeatures(
    point: Point,
  ): Observable<IParcelFeature | null> {
    return this.getQueryLayers('parcel', this.queryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of(null);
        }
        const params = this.getIdentifyWfsParams(point, layers);

        return this.parcelWfsQuery(params, point);
      }),
    );
  }

  getAerialPhotographyLayers() {
    return this.getQueryLayers('ohap', this.queryLayerMap).pipe(take(1));
  }

  private getFeaturesInExtent(
    key: 'surveyLabel' | 'surveyMark',
    latLngBounds: LatLngBounds,
  ) {
    return this.getQueryLayers(key, this.bulkDownloadQueryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of([]);
        }

        const params = this.getIdentifyWfsParams(latLngBounds, layers);

        const searchParams = new URLSearchParams(params);
        const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

        return this.http
          .get<GeoserverQueryResult<GeoJSON.LineString | GeoJSON.Point>>(url)
          .pipe(
            map((result) =>
              result.features.map((feature) => ({
                displayName:
                  feature.properties?.['annotation_text'] ?? // survey label
                  feature.properties?.['display_name'], // survey mark
                id:
                  feature.properties?.['annotation_text']?.toString() ?? // survey label
                  feature.properties?.['mark_id']?.toString(), // survey mark
                type: key,
              })),
            ),
          );
      }),
    );
  }

  private getSurveyLabelFeatures(
    mapBounds: LatLngBounds,
    mapSize: Point,
    clickPosition: Point,
  ): Observable<Omit<ISurveyLabelFeature, 'coords'>[]> {
    return this.getQueryLayers('surveyLabel', this.queryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of([]);
        }
        const params = this.getWMSParams(
          mapBounds,
          mapSize,
          clickPosition,
          layers,
        );

        const searchParams = new URLSearchParams(params);
        const url = `${this.wmsBaseUrl}?${searchParams.toString()}`;

        return this.http
          .get<GeoserverQueryResult<GeoJSON.LineString>>(url)
          .pipe(
            map(
              (result) =>
                result.features?.map(
                  (feature): Omit<ISurveyLabelFeature, 'coords'> => ({
                    type: IdentifyFeatureType.SurveyLabel,
                    name: feature.properties?.['annotation_text'],
                    id: feature.properties?.['annotation_text'],
                    geometry: feature.geometry,
                    crs: result.crs,
                  }),
                ) ?? [],
            ),
          );
      }),
    );
  }

  private getGeomarkGeometries(results: Omit<IPlaceFeature, 'coords'>[]) {
    const featureIds = results.map((f) => f.vicmap_id).join(',');

    const searchParams = new URLSearchParams({
      ...this.getWfsParams('FOI_POLYGON,FOI_LINE'),
      cql_filter: featureIds
        ? `feature_id in (${featureIds});feature_id in (${featureIds})`
        : '1=0;1=0',
      propertyName: 'shape,feature_id',
    });
    const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;
    return this.http.get<GeoserverQueryResult<GeoJSON.Polygon>>(url).pipe(
      map((geomarkResult) => {
        const geomarkMap = new Map<number, GeoJSON.Polygon>(
          geomarkResult.features?.map(({ geometry, properties }) => [
            properties?.['feature_id'],
            geometry,
          ]),
        );
        return results.map((result) => ({
          ...result,
          geometry: geomarkMap.get(result.vicmap_id) || result.geometry,
        }));
      }),
      catchError((error) => {
        console.error('Error fetching FOI geometries', error);
        return of(results);
      }),
    );
  }

  private getPlaceFeatures(
    mapBounds: LatLngBounds,
    mapSize: Point,
    clickPosition: Point,
  ): Observable<Omit<IPlaceFeature, 'coords'>[]> {
    return this.getQueryLayers('place', this.queryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of([]);
        }
        const params = this.getWMSParams(
          mapBounds,
          mapSize,
          clickPosition,
          layers,
        );

        const searchParams = new URLSearchParams(params);
        const url = `${this.wmsBaseUrl}?${searchParams.toString()}`;

        return this.http.get<GeoserverQueryResult<GeoJSON.Point>>(url).pipe(
          map((result) => {
            const groupedFeatures = groupBy(
              result.features?.map((feature) => {
                return {
                  feature: {
                    type: IdentifyFeatureType.Feature,
                    name: feature.properties?.['place_name'],
                    id: feature.properties?.['place_id'],
                    vicmap_id: feature.properties?.['vicmap_id'],
                    geometry: feature.geometry,
                    crs: result.crs,
                  } as Omit<IPlaceFeature, 'coords'>,
                  sortOrder: feature.properties?.['place_name_sort_order'] ?? 0,
                };
              }) ?? [],
              (item) => item.feature.id,
            );

            return Object.values(groupedFeatures).map((features) => {
              return features.sort((a, b) => a.sortOrder - b.sortOrder)[0]
                .feature;
            });
          }),
          switchMap((results) => this.getGeomarkGeometries(results)),
        );
      }),
    );
  }

  public getSurveyMarkLayer(markId: number) {
    const layers = this.queryLayerMap.surveyMark
      .flatMap((queryLayer) => this.processQueryLayer(queryLayer))
      .join(',');

    const params = {
      ...this.getWfsParams(layers),
      propertyName: '',
      cql_filter: `mark_id = ${markId}`,
      count: '1',
    };

    const searchParams = new URLSearchParams(params);
    const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

    return this.http.get<GeoserverQueryResult<GeoJSON.Point>>(url).pipe(
      map((result) => result.features?.[0]),
      map((feature) => {
        if (!feature) {
          throw new Error(`Survey mark with id ${markId} not found`);
        }
        const id = feature.id
          ?.toString()
          .split('.')[0]
          .replace('IDENTIFY_', ''); // extract layer name from feature id

        if (!id) {
          throw new Error(
            `Layer name not found for survey mark with id ${markId}`,
          );
        }
        return id;
      }),
    );
  }

  public getSurveyMarkFeatures(bounds: LatLngBounds): Observable<
    (Omit<ISurveyMarkFeature, 'coords'> & {
      layer_name: string;
    })[]
  > {
    return this.getQueryLayers('surveyMark', this.queryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of([]);
        }
        const params = {
          ...this.getWfsParams(layers),
          bbox: this.boundsToBBox(bounds),
          propertyName: 'shape,mark_id,display_name',
        };

        const searchParams = new URLSearchParams(params);
        const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

        return this.http.get<GeoserverQueryResult<GeoJSON.Point>>(url).pipe(
          map(
            (result) =>
              result.features?.map(
                (
                  feature,
                ): Omit<ISurveyMarkFeature, 'coords'> & {
                  layer_name: string;
                } => ({
                  type: IdentifyFeatureType.SurveyMark,
                  name: feature.properties?.['display_name'],
                  id: Number(feature.properties?.['mark_id']),
                  geometry: feature.geometry,
                  crs: result.crs,
                  layer_name:
                    feature?.id
                      ?.toString()
                      .split('.')[0]
                      .replace('IDENTIFY_', '') ?? '', // extract layer name from feature id
                }),
              ) ?? [],
          ),
          map((results) => uniqueBy(results, 'id')),
        );
      }),
    );
  }

  private getRoadFeatures(
    mapBounds: LatLngBounds,
    mapSize: Point,
    clickPosition: Point,
  ): Observable<Omit<IRoadFeature, 'coords'>[]> {
    return this.getQueryLayers('road', this.queryLayerMap).pipe(
      take(1),
      switchMap((layers) => {
        if (!layers) {
          return of([]);
        }
        const params = this.getWMSParams(
          mapBounds,
          mapSize,
          clickPosition,
          layers,
        );

        const searchParams = new URLSearchParams(params);
        const url = `${this.wmsBaseUrl}?${searchParams.toString()}`;

        return this.http
          .get<GeoserverQueryResult<GeoJSON.LineString>>(url)
          .pipe(
            map(
              (result) =>
                result.features?.map(
                  (feature): Omit<IRoadFeature, 'coords'> => ({
                    type: IdentifyFeatureType.Road,
                    name: feature.properties?.['ezi_road_name'],
                    locality: feature.properties?.['locality'],
                    id: feature.properties?.['locality_road_id'],
                    geometry: feature.geometry,
                    crs: result.crs,
                  }),
                ) ?? [],
            ),
          );
      }),
    );
  }

  getSpearGeometries(
    pfis: string[],
    proposed = false,
  ): Observable<
    { id: string; geometry: GeoJSON.Polygon; crs: GeoserverCRS }[]
  > {
    if (pfis.length === 0) {
      return of([]);
    }

    const ids = pfis.map((id) => `'${id}'`).join(',');

    const searchParams = new URLSearchParams(
      this.getWfsParams(proposed ? 'PROPOSED_PARCEL' : 'PARCEL_MP_APPROVED'),
    );
    searchParams.set(
      'cql_filter',
      proposed ? `pv_pfi in (${ids})` : `parcel_pfi in (${ids})`,
    );
    searchParams.set(
      'propertyName',
      `shape,${proposed ? 'pv_pfi' : 'parcel_pfi'}`,
    );
    const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

    return this.http.get<GeoserverQueryResult<GeoJSON.Polygon>>(url).pipe(
      map((result) =>
        result.features.map((feature) => ({
          id:
            feature.properties?.['parcel_pfi'] ??
            feature.properties?.['pv_pfi'],
          geometry: feature.geometry,
          crs: result.crs,
        })),
      ),
    );
  }

  public getBulkDownloadableFeatures(
    extent: LatLngBounds,
  ): Observable<IBulkDownloadableFeature[]> {
    return forkJoin([
      this.getFeaturesInExtent('surveyLabel', extent),
      this.getFeaturesInExtent('surveyMark', extent),
    ]).pipe(
      map((features) => features.flat()),
      map((features) => uniqueBy(features, 'id')),
    );
  }

  public getFeatures(
    point: Point,
    mapBounds: LatLngBounds,
    mapSize: Point,
    clickPosition: Point,
    clickBounds: LatLngBounds,
  ): Observable<Record<string, IMapFeature[]>> {
    return forkJoin({
      Parcel: this.getIdentifyParcelFeatures(point),
      'Survey Label': this.getSurveyLabelFeatures(
        mapBounds,
        mapSize,
        clickPosition,
      ),
      'Survey Mark': this.getSurveyMarkFeatures(clickBounds),
      Feature: this.getPlaceFeatures(mapBounds, mapSize, clickPosition),
      Road: this.getRoadFeatures(mapBounds, mapSize, clickPosition),
    }).pipe(
      map(({ Parcel, ...rest }) => {
        const features: Record<string, IMapFeature[]> = {};
        if (Parcel) {
          features['Parcel'] = [Parcel];
        }

        Object.keys(rest).forEach((key) => {
          const featureList = rest[key as keyof typeof rest];
          if (featureList.length > 0) {
            features[key] = featureList.map((feature) => ({
              ...feature,
              coords: point,
            }));
          }
        });

        return features;
      }),
    );
  }

  private getParcelWFS(
    params: ReturnType<typeof this.getWfsParams> & { cql_filter: string },
  ) {
    const searchParams = new URLSearchParams(params);
    const url = `${this.wfsBaseUrl}?${searchParams.toString()}`;

    return this.http.get<GeoserverQueryResult<GeoJSON.MultiPolygon>>(url).pipe(
      map((result): Omit<IParcelFeature, 'coords'> | null => {
        if (result.features.length === 0) {
          return null;
        }

        const f = this.getParcelFeatureInfo(result.features[0]);

        return {
          type: IdentifyFeatureType.Parcel,
          geometry: {
            type: 'MultiPolygon',
            coordinates: result.features.flatMap(
              ({ geometry }) => geometry.coordinates,
            ),
          },
          parcels: [{ id: f.id, spi: f.spi }],
          crs: result.crs,
          vPfis: [f.vPfi],
        };
      }),
    );
  }

  private readonly PARCEL_QUERY_LAYER = 'PARCEL_MP_APPROVED';

  public getParcelBy(field: string, value: string) {
    const params = {
      ...this.getWfsParams(this.PARCEL_QUERY_LAYER, 1),
      cql_filter: `${field}='${value}'`,
    };

    return this.getParcelWFS(params);
  }

  /**
   * Returns a set of active layer groups based on the current layers map.
   * Each group corresponds to a key in the provided layerMap.
   */
  private getActiveLayerGroups(
    layerMap: QueryLayerMap,
  ): Observable<Set<keyof QueryLayerMap>> {
    return this.layersMap$.pipe(
      take(1),
      map((layersMap) => {
        const activeGroups = new Set<keyof QueryLayerMap>();

        (Object.keys(layerMap) as Array<keyof QueryLayerMap>).forEach(
          (groupKey) => {
            const groupLayers = layerMap[groupKey];
            const layerIds = groupLayers.map((item) =>
              typeof item === 'string' ? item : item.id,
            );

            if (
              layerIds.some((layerId) => layersMap[layerId]?.status === true)
            ) {
              activeGroups.add(groupKey);
            }
          },
        );

        return activeGroups;
      }),
    );
  }

  /**
   * Returns active layer groups for broadcast message audience determination.
   */
  public getBroadcastMsgAudienceLayerGroups(): Observable<
    Set<keyof QueryLayerMap>
  > {
    return this.getActiveLayerGroups(this.broadcastMsgAudienceLayerMap);
  }
}
